import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Film, 
  ArrowRight, 
  ArrowLeft,
  Sparkles,
  Target,
  Users,
  Trophy,
  Briefcase,
  Heart,
  ChevronDown,
  Check,
  Loader2,
  Globe,
  Music,
  Clapperboard
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Onboarding = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Form state
  const [referralSource, setReferralSource] = useState('');
  const [referralDetail, setReferralDetail] = useState('');
  const [country, setCountry] = useState('');
  const [primaryGoal, setPrimaryGoal] = useState('');
  const [experienceLevel, setExperienceLevel] = useState('');
  const [projectType, setProjectType] = useState('');
  const [storytellingStyle, setStorytellingStyle] = useState('');

  const countries = [
    "United States", "United Kingdom", "Canada", "Australia", "India", "Nigeria", 
    "South Africa", "Ghana", "Kenya", "Germany", "France", "Spain", "Italy", 
    "Brazil", "Mexico", "Argentina", "Japan", "South Korea", "Philippines", 
    "Indonesia", "Malaysia", "Singapore", "United Arab Emirates", "Saudi Arabia",
    "Egypt", "Morocco", "Netherlands", "Belgium", "Sweden", "Norway", "Denmark",
    "Ireland", "New Zealand", "Pakistan", "Bangladesh", "Sri Lanka", "Nepal",
    "Thailand", "Vietnam", "Poland", "Turkey", "Israel", "Russia", "Ukraine",
    "Portugal", "Greece", "Switzerland", "Austria", "Czech Republic", "Romania",
    "Hungary", "Colombia", "Chile", "Peru", "Venezuela", "Jamaica", "Trinidad and Tobago",
    "Other"
  ].sort();

  const referralSources = [
    { value: 'instagram', label: 'Instagram', needsDetail: true },
    { value: 'tiktok', label: 'TikTok', needsDetail: true },
    { value: 'youtube', label: 'YouTube', needsDetail: true },
    { value: 'twitter', label: 'Twitter/X', needsDetail: true },
    { value: 'facebook', label: 'Facebook', needsDetail: true },
    { value: 'google', label: 'Google Search', needsDetail: false },
    { value: 'friend', label: 'Friend or Colleague', needsDetail: true },
    { value: 'podcast', label: 'Podcast', needsDetail: true },
    { value: 'blog', label: 'Blog or Article', needsDetail: false },
    { value: 'ad', label: 'Online Ad', needsDetail: false },
    { value: 'other', label: 'Other', needsDetail: true }
  ];

  const storytellingStyles = [
    { 
      value: 'hollywood', 
      label: 'Hollywood / Western', 
      icon: Clapperboard,
      color: 'from-amber-500/20 to-amber-600/10',
      borderColor: 'border-amber-500/30',
      textColor: 'text-amber-400',
      description: 'Classic 3-act structure, fast pacing, individual hero journeys'
    },
    { 
      value: 'bollywood', 
      label: 'Bollywood / Indian', 
      icon: Music,
      color: 'from-orange-500/20 to-pink-500/10',
      borderColor: 'border-orange-500/30',
      textColor: 'text-orange-400',
      description: 'Emotional drama, musical elements, family sagas, romantic storytelling'
    },
    { 
      value: 'nollywood', 
      label: 'Nollywood / African', 
      icon: Globe,
      color: 'from-green-500/20 to-emerald-500/10',
      borderColor: 'border-green-500/30',
      textColor: 'text-green-400',
      description: 'Community focus, moral themes, cultural traditions, family dynamics'
    },
    { 
      value: 'universal', 
      label: 'Universal / Blend', 
      icon: Sparkles,
      color: 'from-purple-500/20 to-blue-500/10',
      borderColor: 'border-purple-500/30',
      textColor: 'text-purple-400',
      description: 'Mix of styles, global appeal, flexible storytelling approach'
    }
  ];

  const goals = [
    { value: 'first_script', label: 'Write my first screenplay', icon: Sparkles },
    { value: 'finish_script', label: 'Finally finish a script I started', icon: Target },
    { value: 'improve_skills', label: 'Improve my screenwriting skills', icon: Trophy },
    { value: 'professional', label: 'Break into the industry professionally', icon: Briefcase },
    { value: 'fun', label: 'Write for fun and creativity', icon: Heart },
    { value: 'competition', label: 'Enter the Summer Competition', icon: Trophy }
  ];

  const experienceLevels = [
    { value: 'beginner', label: 'Complete beginner - Never written a script' },
    { value: 'novice', label: 'Novice - Written a few scenes or shorts' },
    { value: 'intermediate', label: 'Intermediate - Completed 1-2 full scripts' },
    { value: 'experienced', label: 'Experienced - Multiple completed scripts' },
    { value: 'professional', label: 'Professional - Produced or sold work' }
  ];

  const projectTypes = [
    { value: 'feature', label: 'Feature Film (90-120 pages)' },
    { value: 'short', label: 'Short Film (under 30 pages)' },
    { value: 'series', label: 'TV Series Pilot' },
    { value: 'web_series', label: 'Web Series' },
    { value: 'not_sure', label: 'Not sure yet - exploring ideas' }
  ];

  const selectedSourceNeedsDetail = referralSources.find(s => s.value === referralSource)?.needsDetail;

  const handleSubmit = async () => {
    setLoading(true);
    
    try {
      const token = localStorage.getItem('moviebot_token');
      const response = await fetch(`${API_URL}/api/user/onboarding`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          referral_source: referralSource,
          referral_detail: referralDetail,
          country: country,
          primary_goal: primaryGoal,
          experience_level: experienceLevel,
          project_type: projectType,
          storytelling_style: storytellingStyle
        })
      });

      if (response.ok) {
        // Update user context to mark onboarding complete and store storytelling style
        updateUser({ onboarding_completed: true, storytelling_style: storytellingStyle });
        toast.success('Welcome to MovieBot! Let\'s create your first project.');
        navigate('/new-project');
      } else {
        toast.error('Failed to save preferences');
      }
    } catch (error) {
      console.error('Onboarding error:', error);
      toast.error('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const canProceedStep1 = referralSource && (!selectedSourceNeedsDetail || referralDetail) && country;
  const canProceedStep2 = storytellingStyle;
  const canProceedStep3 = primaryGoal && experienceLevel && projectType;

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-xl w-full"
      >
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <Film className="w-8 h-8 text-primary" />
          <span className="font-black text-xl tracking-tight text-white">MOVIEBOT</span>
        </div>

        {/* Progress */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className={`w-3 h-3 rounded-full ${step >= 1 ? 'bg-primary' : 'bg-white/20'}`} />
          <div className={`w-8 h-0.5 ${step >= 2 ? 'bg-primary' : 'bg-white/20'}`} />
          <div className={`w-3 h-3 rounded-full ${step >= 2 ? 'bg-primary' : 'bg-white/20'}`} />
          <div className={`w-8 h-0.5 ${step >= 3 ? 'bg-primary' : 'bg-white/20'}`} />
          <div className={`w-3 h-3 rounded-full ${step >= 3 ? 'bg-primary' : 'bg-white/20'}`} />
        </div>

        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-secondary/30 border border-white/10 p-8"
            >
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-white mb-2">
                  Welcome, {user?.name?.split(' ')[0] || 'Writer'}!
                </h2>
                <p className="text-muted-foreground">
                  Quick question before we get started
                </p>
              </div>

              {/* How did you hear about us */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-white mb-3">
                  How did you hear about MovieBot?
                </label>
                <div className="relative">
                  <select
                    value={referralSource}
                    onChange={(e) => {
                      setReferralSource(e.target.value);
                      setReferralDetail('');
                    }}
                    className="w-full bg-black/50 border border-white/20 text-white p-4 pr-10 appearance-none cursor-pointer focus:border-primary focus:outline-none"
                    data-testid="referral-source-select"
                  >
                    <option value="">Select an option...</option>
                    {referralSources.map(source => (
                      <option key={source.value} value={source.value}>
                        {source.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none" />
                </div>
              </div>

              {/* Follow-up question for social media */}
              {selectedSourceNeedsDetail && referralSource && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="mb-6"
                >
                  <label className="block text-sm font-medium text-white mb-3">
                    {referralSource === 'friend' 
                      ? "Who referred you? (optional)"
                      : referralSource === 'podcast'
                        ? "Which podcast?"
                        : referralSource === 'other'
                          ? "Please specify"
                          : "Whose page or account?"}
                  </label>
                  <input
                    type="text"
                    value={referralDetail}
                    onChange={(e) => setReferralDetail(e.target.value)}
                    placeholder={
                      referralSource === 'friend' 
                        ? "Their name"
                        : referralSource === 'podcast'
                          ? "Podcast name"
                          : "@username or page name"
                    }
                    className="w-full bg-black/50 border border-white/20 text-white p-4 focus:border-primary focus:outline-none placeholder:text-muted-foreground/50"
                    data-testid="referral-detail-input"
                  />
                </motion.div>
              )}

              {/* Country Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-white mb-3">
                  Where are you located?
                </label>
                <div className="relative">
                  <select
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    className="w-full bg-black/50 border border-white/20 text-white p-4 pr-10 appearance-none cursor-pointer focus:border-primary focus:outline-none"
                    data-testid="country-select"
                  >
                    <option value="">Select your country...</option>
                    {countries.map(c => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none" />
                </div>
              </div>

              <Button
                onClick={() => setStep(2)}
                disabled={!canProceedStep1}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-bold py-4"
                data-testid="onboarding-next-btn"
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-secondary/30 border border-white/10 p-8"
            >
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-white mb-2">
                  What's your storytelling style?
                </h2>
                <p className="text-muted-foreground">
                  This helps our AI tailor suggestions to your creative vision
                </p>
              </div>

              {/* Storytelling Style Selection */}
              <div className="space-y-3 mb-6">
                {storytellingStyles.map(style => {
                  const Icon = style.icon;
                  const isSelected = storytellingStyle === style.value;
                  return (
                    <button
                      key={style.value}
                      onClick={() => setStorytellingStyle(style.value)}
                      className={`w-full p-4 text-left border transition-all rounded-lg ${
                        isSelected
                          ? `bg-gradient-to-r ${style.color} ${style.borderColor} border-2`
                          : 'border-white/10 bg-black/30 hover:border-white/30'
                      }`}
                      data-testid={`style-${style.value}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          isSelected ? `bg-gradient-to-r ${style.color}` : 'bg-white/10'
                        }`}>
                          <Icon className={`w-5 h-5 ${isSelected ? style.textColor : 'text-muted-foreground'}`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className={`font-bold ${isSelected ? 'text-white' : 'text-muted-foreground'}`}>
                              {style.label}
                            </span>
                            {isSelected && (
                              <Check className={`w-4 h-4 ${style.textColor}`} />
                            )}
                          </div>
                          <p className={`text-sm mt-1 ${isSelected ? 'text-white/70' : 'text-muted-foreground/70'}`}>
                            {style.description}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => setStep(1)}
                  variant="outline"
                  className="flex-1 border-white/20 text-white hover:bg-white/10 py-4"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button
                  onClick={() => setStep(3)}
                  disabled={!canProceedStep2}
                  className="flex-[2] bg-primary text-primary-foreground hover:bg-primary/90 font-bold py-4"
                  data-testid="onboarding-style-next-btn"
                >
                  Continue
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-secondary/30 border border-white/10 p-8"
            >
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-white mb-2">
                  Tell us about your goals
                </h2>
                <p className="text-muted-foreground">
                  This helps us personalize your experience
                </p>
              </div>

              {/* Primary Goal */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-white mb-3">
                  What's your main goal with MovieBot?
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {goals.map(goal => {
                    const Icon = goal.icon;
                    return (
                      <button
                        key={goal.value}
                        onClick={() => setPrimaryGoal(goal.value)}
                        className={`p-4 text-left border transition-all ${
                          primaryGoal === goal.value
                            ? 'border-primary bg-primary/10 text-white'
                            : 'border-white/10 bg-black/30 text-muted-foreground hover:border-white/30'
                        }`}
                        data-testid={`goal-${goal.value}`}
                      >
                        <Icon className={`w-5 h-5 mb-2 ${primaryGoal === goal.value ? 'text-primary' : ''}`} />
                        <span className="text-sm">{goal.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Experience Level */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-white mb-3">
                  What's your screenwriting experience?
                </label>
                <div className="relative">
                  <select
                    value={experienceLevel}
                    onChange={(e) => setExperienceLevel(e.target.value)}
                    className="w-full bg-black/50 border border-white/20 text-white p-4 pr-10 appearance-none cursor-pointer focus:border-primary focus:outline-none"
                    data-testid="experience-select"
                  >
                    <option value="">Select your level...</option>
                    {experienceLevels.map(level => (
                      <option key={level.value} value={level.value}>
                        {level.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none" />
                </div>
              </div>

              {/* Project Type */}
              <div className="mb-8">
                <label className="block text-sm font-medium text-white mb-3">
                  What type of project do you want to create?
                </label>
                <div className="relative">
                  <select
                    value={projectType}
                    onChange={(e) => setProjectType(e.target.value)}
                    className="w-full bg-black/50 border border-white/20 text-white p-4 pr-10 appearance-none cursor-pointer focus:border-primary focus:outline-none"
                    data-testid="project-type-select"
                  >
                    <option value="">Select project type...</option>
                    {projectTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none" />
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => setStep(2)}
                  variant="outline"
                  className="flex-1 border-white/20 text-white hover:bg-white/10 py-4"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={!canProceedStep3 || loading}
                  className="flex-[2] bg-primary text-primary-foreground hover:bg-primary/90 font-bold py-4"
                  data-testid="onboarding-complete-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      Start Creating
                      <Sparkles className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Onboarding is required - no skip option */}
      </motion.div>
    </div>
  );
};

export default Onboarding;
